// ---------------------------------------------------------------------------
// Workspace — top-level scoping boundary for every Workflow Service resource.
// Mirrors the Workspace section of api-spec.md.
// ---------------------------------------------------------------------------

import type { SystemProperties } from './common.js';

// Kebab-case pattern per spec: lowercase letter start, then letters/digits/hyphens.
export const WORKSPACE_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;
export const WORKSPACE_NAME_MAX_LENGTH = 63;

export interface WorkspaceAccessControl {
  aclAnchorDatasetId: string;
  publisherAppIds: string[];
}

export type WorkspaceEffectivePermission =
  | 'read'
  | 'write'
  | 'manageAccess'
  | 'publishEvaluation';

export interface WorkspaceSummary {
  name: string; // kebab-case, immutable, from URL
  displayName?: string;
  description?: string;
  teamsPulseDashboardId?: string | null;
  accessControl?: WorkspaceAccessControl | null;
  systemProperties?: SystemProperties;
}

export interface Workspace extends WorkspaceSummary {
  effectivePermission?: WorkspaceEffectivePermission;
  etag?: string;
  systemProperties: SystemProperties;
}

// Body of PUT /workspaces/{name}.
export interface WorkspaceWriteBody {
  displayName?: string;
  description?: string;
  teamsPulseDashboardId?: string;
  accessControl?: WorkspaceAccessControl;
}

// Body of PATCH /workspaces/{name} — only the mutable subset.
export interface WorkspacePatchBody {
  displayName?: string;
  description?: string;
  teamsPulseDashboardId?: string;
  accessControl?:
    | Pick<WorkspaceAccessControl, 'publisherAppIds'>
    | WorkspaceAccessControl;
}

export function isValidWorkspaceName(name: string): boolean {
  return (
    name.length > 0 &&
    name.length <= WORKSPACE_NAME_MAX_LENGTH &&
    WORKSPACE_NAME_PATTERN.test(name)
  );
}
